// Cache patches (CHAT-PERF): write a known row straight into the TanStack Query cache instead
// of refetching. Two callers: payload-carrying change pings (live/change-pings.ts: the server
// sends the row, already filtered for this viewer) and optimistic mutations
// (api/optimistic.ts: the row the client expects, reconciled when the server answers).
//
// The helpers are shape-aware but not module-aware: a list query's data is either a page
// (`{ data: T[] }`) or TanStack's `InfiniteData` of pages, and a row is matched by a key
// function (its id, or for optimistic rows the client id the server echoes back). Work can use
// the same functions for tasks; chat's wrappers for messages are at the bottom.
//
// Ordering: list pages are oldest first within a page, and an infinite list's `pages[0]` is the
// newest page (messages.list, tasks lists sorted by time). A new row goes into the newest page
// at its sorted place, never into an older page that is not the newest (it would belong to a
// page boundary the client cannot see); a row already present is replaced where it is.
import type { InfiniteData, QueryClient, QueryKey } from "@tanstack/react-query";
import type { MessageView } from "../features/chat/chat-queries";

export type Page<T> = { data: T[] } & Record<string, unknown>;
export type ListData<T> = Page<T> | InfiniteData<Page<T>, unknown>;

const isInfinite = <T>(d: ListData<T>): d is InfiniteData<Page<T>, unknown> =>
  Array.isArray((d as InfiniteData<Page<T>>).pages);

/** The rows of a page or of every page (newest page first, as stored). */
export function rowsOf<T>(d: ListData<T> | undefined): T[] {
  if (!d) return [];
  return isInfinite(d) ? d.pages.flatMap((p) => p.data) : d.data;
}

/** Applies `fn` to each page's rows; returns the same object when nothing changed. */
export function mapPages<T>(d: ListData<T>, fn: (rows: T[], newest: boolean) => T[]): ListData<T> {
  if (isInfinite(d)) {
    let changed = false;
    const pages = d.pages.map((p, i) => {
      const next = fn(p.data, i === 0);
      if (next === p.data) return p;
      changed = true;
      return { ...p, data: next };
    });
    return changed ? { ...d, pages } : d;
  }
  const next = fn(d.data, true);
  return next === d.data ? d : { ...d, data: next };
}

export type UpsertOptions<T> = {
  /** Identity of a row (default `id`). */
  key?: (row: T) => string;
  /** Other identities that mean "the same row" (e.g. an optimistic row's client id). */
  aliases?: (row: T) => readonly (string | null | undefined)[];
  /** Sort position for a new row (ascending); omitted: appended at the end of the newest page. */
  order?: (a: T, b: T) => number;
  /** Insert when absent (default true); false only replaces a row already shown. */
  insert?: boolean;
};

const defaultKey = (row: unknown) => String((row as { id?: unknown }).id);

/**
 * Upserts `row` into list data: replaces the row with the same key (or an alias of it),
 * otherwise inserts it into the newest page at its sorted place. Pure; the same object back when
 * nothing changed.
 */
export function upsertRow<T>(d: ListData<T>, row: T, o: UpsertOptions<T> = {}): ListData<T> {
  const key = o.key ?? defaultKey;
  const ids = new Set([key(row), ...(o.aliases?.(row) ?? [])].filter(Boolean) as string[]);
  const matches = (r: T) => ids.has(key(r)) || (o.aliases?.(r) ?? []).some((a) => a && ids.has(a));
  let found = false;
  const replaced = mapPages(d, (rows) => {
    const at = rows.findIndex(matches);
    if (at < 0) return rows;
    // A second copy (e.g. the optimistic row and the pinged row both present) is dropped.
    const rest = rows.filter((r, i) => i === at || !matches(r));
    found = true;
    const keep = rest.findIndex(matches);
    return rest.map((r, i) => (i === keep ? row : r));
  });
  if (found || o.insert === false) return replaced;
  return mapPages(replaced, (rows, newest) => {
    if (!newest) return rows;
    if (!o.order) return [...rows, row];
    const first = rows[0];
    // Older than the newest page's first row: it belongs to a page not on screen.
    if (first && o.order(row, first) < 0 && isInfinite(d) && d.pages.length > 1) return rows;
    const at = rows.findIndex((r) => (o.order as (a: T, b: T) => number)(row, r) < 0);
    return at < 0 ? [...rows, row] : [...rows.slice(0, at), row, ...rows.slice(at)];
  });
}

/** Removes the rows `pred` matches. */
export function removeRows<T>(d: ListData<T>, pred: (row: T) => boolean): ListData<T> {
  return mapPages(d, (rows) => (rows.some(pred) ? rows.filter((r) => !pred(r)) : rows));
}

/** Patches every cached list under `prefix` (a key prefix) with `fn`; skips empty queries. */
export function patchLists<T>(
  queryClient: QueryClient,
  prefix: QueryKey,
  fn: (d: ListData<T>) => ListData<T>,
): void {
  queryClient.setQueriesData<ListData<T>>({ queryKey: prefix }, (old) =>
    old && typeof old === "object" && ("data" in old || "pages" in old) ? fn(old) : old,
  );
}

/** Every `[key, data]` under `prefix`, for a rollback snapshot. */
export function snapshot(queryClient: QueryClient, prefix: QueryKey) {
  return queryClient.getQueriesData({ queryKey: prefix });
}

/** Puts a snapshot back (a failed optimistic write). */
export function restore(
  queryClient: QueryClient,
  snap: readonly (readonly [QueryKey, unknown])[],
): void {
  for (const [key, data] of snap) queryClient.setQueryData(key, data);
}

// Chat ------------------------------------------------------------------------------------------

/** A message as the client holds it: an optimistic one carries its send state. */
export type LocalMessage = MessageView & {
  local?: { state: "sending" | "failed"; error?: string };
};

const messageOrder = (a: MessageView, b: MessageView) =>
  a.created_at === b.created_at
    ? a.id < b.id
      ? -1
      : a.id > b.id
        ? 1
        : 0
    : a.created_at < b.created_at
      ? -1
      : 1;

const messageOptions: UpsertOptions<LocalMessage> = {
  aliases: (m) => [m.client_id ? `client:${m.client_id}` : null],
  order: messageOrder,
};

/** Every cached message list of a thread (the paged list and the channel stream's page). */
export const messagesPrefix = (co: string, threadId: string): QueryKey => [
  "company",
  co,
  "chat",
  "messages",
  threadId,
];

/**
 * Writes a message into its thread's cached lists: the server's copy replaces the optimistic
 * one with the same client id, and a newer copy (an edit, a reaction, a delete) replaces the
 * shown one in place.
 */
export function patchMessage(
  queryClient: QueryClient,
  co: string,
  m: LocalMessage,
  o: { insert?: boolean } = {},
): void {
  patchLists<LocalMessage>(queryClient, messagesPrefix(co, m.thread_id), (d) =>
    upsertRow(d, m, { ...messageOptions, ...(o.insert === false ? { insert: false } : {}) }),
  );
}

/** Rewrites one cached message (by id) of a thread; no-op when it is not shown. */
export function updateMessage(
  queryClient: QueryClient,
  co: string,
  threadId: string,
  id: string,
  fn: (m: LocalMessage) => LocalMessage,
): void {
  patchLists<LocalMessage>(queryClient, messagesPrefix(co, threadId), (d) =>
    mapPages(d, (rows) => {
      const at = rows.findIndex((r) => r.id === id);
      if (at < 0) return rows;
      const next = [...rows];
      next[at] = fn(rows[at] as LocalMessage);
      return next;
    }),
  );
}

/** The cached copy of a message (any list of its thread), if one is shown. */
export function cachedMessage(
  queryClient: QueryClient,
  co: string,
  threadId: string,
  id: string,
): LocalMessage | undefined {
  for (const [, d] of queryClient.getQueriesData<ListData<LocalMessage>>({
    queryKey: messagesPrefix(co, threadId),
  })) {
    const hit = rowsOf(d).find((m) => m.id === id);
    if (hit) return hit;
  }
  return undefined;
}
